import React, { ComponentType, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import { Company, EntityType } from "@mapmycustomers/shared/types/entity";
import { LoadingSpinner } from "@mapmycustomers/ui";

import ResizableDrawer from "@app/component/ResizableDrawer";
import localSettings from "@app/config/LocalSettings";
import { EnterPinLocationModePayload } from "@app/scene/map/store/pinLocation/actions";
import { RootState } from "@app/store/rootReducer";
import { EntityWithLocationProps } from "@app/types/map/EntityWithLocationProps";
import getPageContentContainer from "@app/util/ui/getPageContentContainer";
import useHiddenZendesk from "@app/util/useHiddenZendesk";

import Title from "../components/Title";
import consts from "../styles/_consts.module.scss";

import styles from "./CompanyRecordPane.module.scss";
import Content from "./Content";
import { changeActivitiesFilter, fetchPreviewData, setHasChangesFlag } from "./store/actions";
import CompanyRecordData from "./store/CompanyRecordData";
import { getRecordData, isLoading } from "./store/selectors";

export interface CompanyRecordPaneProps {
  className?: string;
  // We're requiring company id instead of a full company, because sometimes we only partially
  // load companies (like only load id and name sometimes), and since we can't provide a full
  // company, preview pane will just fail. And I don't want to require component consumers to
  // guarantee a full company is provided. Instead, component can load company details itself.
  companyId: Company["id"];
  isMapMode?: boolean;
  locationEditComponent?: ComponentType<EntityWithLocationProps>;
  onChange?: (hasChanges: boolean) => void;
  onChangeCompanyColor?: (color: Company["color"]) => void;
  onChangeCompanyShape?: (color: Company["shape"]) => void;
  onEnterPinLocationMode?: (payload: EnterPinLocationModePayload) => void;
  onHide: () => void;
  onShouldShowPrompt: (shouldShow: boolean) => void;
  zIndex?: number;
}

interface Props extends CompanyRecordPaneProps {
  changeActivitiesFilter: typeof changeActivitiesFilter;
  fetchPreviewData: typeof fetchPreviewData.request;
  loading: boolean;
  recordData: CompanyRecordData;
  setHasChangesFlag: typeof setHasChangesFlag;
}

const CompanyRecordPane: React.FC<Props> = ({
  changeActivitiesFilter,
  className,
  companyId,
  fetchPreviewData,
  isMapMode,
  loading,
  locationEditComponent,
  onChangeCompanyColor,
  onChangeCompanyShape,
  onEnterPinLocationMode,
  onHide,
  onShouldShowPrompt,
  recordData,
  setHasChangesFlag,
  zIndex,
}) => {
  useHiddenZendesk();
  useEffect(() => {
    // We reset it here to avoid having any activities filter in the store when Activities tab is opened next time.
    // Filters themselves are not lost, they're passed down to EditActivities in activitiesFilter prop
    // before reset happens.
    return () => {
      changeActivitiesFilter?.(ActivityStatusOption.ALL);
    };
  }, [changeActivitiesFilter]);

  const defaultRecordPaneWidth = localSettings.getRecordPaneWidth();
  const [detailsFormChanged, setDetailsFormChanged] = useState(false);
  const [notesChanged, setNotesChanged] = useState(false);
  const isAnythingChanged = detailsFormChanged || notesChanged;
  const { company } = recordData;
  const handleOnResize = useCallback((width) => localSettings.setRecordPaneWidth(width), []);

  useEffect(() => {
    onShouldShowPrompt(isAnythingChanged);
    setHasChangesFlag(isAnythingChanged);
  }, [isAnythingChanged, onShouldShowPrompt, setHasChangesFlag]);

  useEffect(() => {
    fetchPreviewData(companyId);
    setDetailsFormChanged(false);
  }, [companyId, fetchPreviewData, setDetailsFormChanged]);

  useEffect(() => {
    if (company?.color === undefined) {
      return;
    }
    onChangeCompanyColor?.(company.color);
  }, [company?.color, onChangeCompanyColor]);

  useEffect(() => {
    if (company?.shape === undefined) {
      return;
    }
    onChangeCompanyShape?.(company.shape);
  }, [company?.shape, onChangeCompanyShape]);

  return (
    <ResizableDrawer
      className={cn(styles.container, className)}
      closable
      defaultWidth={defaultRecordPaneWidth}
      disableResize={isMapMode}
      getContainer={zIndex || isMapMode ? false : getPageContentContainer()}
      mask={false}
      maxWidth={parseInt(consts.maxPaneWidth, 10)}
      onClose={onHide}
      onResize={handleOnResize}
      open
      placement="right"
      title={company ? <Title entity={company} entityType={EntityType.COMPANY} /> : <br />}
      zIndex={zIndex}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Content
          formChanged={detailsFormChanged}
          isMapMode={Boolean(isMapMode)}
          locationEditComponent={locationEditComponent}
          onEnterPinLocationMode={onEnterPinLocationMode}
          onFormChange={setDetailsFormChanged}
          onHide={onHide}
          onNotesChange={setNotesChanged}
          recordData={recordData}
        />
      )}
    </ResizableDrawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: isLoading(state),
  recordData: getRecordData(state),
});

const mapDispatchToProps = {
  changeActivitiesFilter,
  fetchPreviewData: fetchPreviewData.request,
  setHasChangesFlag,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyRecordPane);
